<!-- 学校内班级级联选择（校区/学段/年级/班级）。创建于 2026-08-07；Id 保持字符串。最后修改：2026-10-18 -->
<template>
  <el-cascader
    :key="renderKey"
    v-model="innerPath"
    :options="options"
    :props="cascaderProps"
    filterable
    clearable
    :placeholder="placeholder"
    :size="size"
    :disabled="disabled || loading"
    style="width: 100%"
    @change="onPathChange"
  />
</template>

<script>
import { getTeachingDepartmentTree } from '../api/modules/department'
import {
  getDeptCascaderProps,
  prepareDeptCascaderTree,
  normalizeDeptId,
  deptIdsEqual,
} from '../utils/deptCascaderProps'

export default {
  name: 'ClassCascaderSelect',
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    schoolId: {
      type: [Number, String],
      default: 0
    },
    placeholder: {
      type: String,
      default: '请选择目标班级'
    },
    size: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      options: [],
      innerPath: [],
      loading: false,
      loadedSchoolId: '',
      renderKey: 0,
      // 与全站部门级联统一：可展开 disabled 上级。最后更新：2026-08-26
      cascaderProps: getDeptCascaderProps({ emitPath: true })
    }
  },
  watch: {
    schoolId: {
      immediate: true,
      handler(val) {
        this.loadOptions(normalizeDeptId(val))
      }
    },
    value() {
      this.syncPathFromValue()
    }
  },
  methods: {
    loadOptions(schoolId) {
      if (!schoolId) {
        this.options = []
        this.innerPath = []
        this.loadedSchoolId = ''
        return
      }
      if (this.loadedSchoolId === schoolId && this.options.length) {
        this.syncPathFromValue()
        return
      }
      this.loading = true
      getTeachingDepartmentTree({
        pid: 0,
        type: 0,
        scopeDepartmentId: schoolId
      }).then(res => {
        this.loading = false
        const root = res && res.data && res.data.response
        const schoolNode = this.findSchoolNode(root, schoolId) || root
        const built = this.toClassCascaderNode(schoolNode)
        // 从学校下级开始，避免再显示「总平台 / 学校」
        this.options = built && built.children && built.children.length
          ? built.children
          : (built ? [built] : [])
        this.options = prepareDeptCascaderTree(this.options)
        this.loadedSchoolId = schoolId
        this.renderKey += 1
        this.syncPathFromValue()
      }).catch(() => {
        this.loading = false
        this.options = []
        this.loadedSchoolId = ''
        this.innerPath = []
      })
    },
    findSchoolNode(node, schoolId) {
      if (!node) return null
      const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
      const val = normalizeDeptId(node.value)
      if (deptIdsEqual(val, schoolId) && (orgType === 'school' || orgType === 'affiliateschool')) {
        return node
      }
      const children = node.children || []
      for (let i = 0; i < children.length; i += 1) {
        const found = this.findSchoolNode(children[i], schoolId)
        if (found) return found
      }
      return null
    },
    // 仅保留通向班级的链路，剪掉无班级枝干
    toClassCascaderNode(node) {
      if (!node || node.value == null) return null
      const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
      const nodeId = normalizeDeptId(node.value)
      if (!nodeId) return null
      const children = (node.children || [])
        .map(child => this.toClassCascaderNode(child))
        .filter(Boolean)
      const isClass = orgType === 'class' || (Number(node.BigType) === 1 && !(node.children || []).length)
      if (isClass) {
        return {
          value: nodeId,
          label: node.label || node.Name || nodeId
        }
      }
      if (!children.length) return null
      return {
        value: nodeId,
        label: node.label || node.Name || nodeId,
        children
      }
    },
    findPath(nodes, targetId, path = []) {
      const list = nodes || []
      for (let i = 0; i < list.length; i += 1) {
        const node = list[i]
        const nodeId = normalizeDeptId(node.value)
        const next = path.concat(nodeId)
        if (deptIdsEqual(nodeId, targetId)) return next
        if (node.children && node.children.length) {
          const found = this.findPath(node.children, targetId, next)
          if (found && found.length) return found
        }
      }
      return []
    },
    syncPathFromValue() {
      const id = normalizeDeptId(this.value)
      if (!id) {
        this.innerPath = []
        return
      }
      const path = this.findPath(this.options, id)
      this.innerPath = path.length ? path : []
    },
    onPathChange(path) {
      if (!path || !path.length) {
        this.emitValue(null)
        return
      }
      this.emitValue(normalizeDeptId(path[path.length - 1]) || null)
    },
    emitValue(id) {
      this.$emit('input', id)
      this.$emit('change', id)
    }
  }
}
</script>
